import type { PromotionConfig, PromotionDraftType, PromotionTimeLimit, PromotionType } from "../types/onboarding.ts";
import { isSingleCheckoutQuantityMode, type TemplateFamilyMetadata } from "../templateStudio/templateFamilyMetadata.ts";

type SubmittablePromotionConfig = Omit<PromotionConfig, "type"> & {
  type: Exclude<PromotionType, "combo">;
};

export const PROMOTION_PRESETS: PromotionConfig[] = [
  { enabled: true, type: "buy_x_get_y", label: "Paga 1 lleva 2", limitTime: false, timeLimit: "unlimited", paidUnits: 1, receivedUnits: 2 },
  { enabled: true, type: "discount", label: "50% off solo por hoy", limitTime: true, timeLimit: "midnight", discountPercentage: 50 },
  { enabled: true, type: "buy_x_get_y", label: "Paga 3 lleva 5", limitTime: false, timeLimit: "unlimited", paidUnits: 3, receivedUnits: 5 },
];

export const createDefaultPromotion = (index = 0): PromotionConfig => ({
  ...PROMOTION_PRESETS[index % PROMOTION_PRESETS.length],
});

export const createEmptyPromotion = (): PromotionConfig => ({
  enabled: true,
  type: "",
  label: "",
  limitTime: false,
  timeLimit: "unlimited",
});

export const isConfiguredPromotionType = (
  type: PromotionDraftType | null | undefined
): type is Exclude<PromotionType, "combo"> => (
  type === "discount" || type === "buy_x_get_y"
);

export const getSubmittablePromotion = (
  promotion: PromotionConfig | null | undefined,
  familyMetadata?: TemplateFamilyMetadata | null,
): SubmittablePromotionConfig | null => {
  if (!promotion?.enabled || !isConfiguredPromotionType(promotion.type)) {
    return null;
  }
  if (isSingleCheckoutQuantityMode(familyMetadata) && promotion.type === "buy_x_get_y") {
    return null;
  }
  const normalized = normalizePromotion(promotion);
  return {
    ...normalized,
    type: normalized.type === "buy_x_get_y" ? "buy_x_get_y" : "discount",
  };
};

export const normalizePromotion = (
  promotion: PromotionConfig | null | undefined,
  fallbackIndex = 0
): PromotionConfig => {
  const base = promotion?.enabled ? promotion : createDefaultPromotion(fallbackIndex);
  const timeLimit = normalizePromotionTimeLimit(base.timeLimit, base.limitTime);
  if (base.type === "buy_x_get_y") {
    const paidUnits = Math.max(1, Number(base.paidUnits || 1));
    const receivedUnits = Math.max(paidUnits + 1, Number(base.receivedUnits || paidUnits + 1));
    return {
      enabled: true,
      type: "buy_x_get_y",
      label: buildPromotionLabel({ type: "buy_x_get_y", paidUnits, receivedUnits }),
      limitTime: timeLimit !== "unlimited",
      timeLimit,
      paidUnits,
      receivedUnits,
    };
  }
  if (base.type === "discount") {
    const discountPercentage = Math.min(95, Math.max(1, Number(base.discountPercentage || 50)));
    return {
      enabled: true,
      type: "discount",
      label: buildPromotionLabel({ type: "discount", discountPercentage, timeLimit }),
      limitTime: timeLimit !== "unlimited",
      timeLimit,
      discountPercentage,
    };
  }
  return normalizePromotion(createDefaultPromotion(fallbackIndex), fallbackIndex);
};

export const getPromotionTypeLabel = (type: PromotionType) => {
  if (type === "buy_x_get_y") return "Compra X lleva Y";
  if (type === "discount") return "Descuento";
  return "Combo";
};

export const normalizePromotionTimeLimit = (
  timeLimit?: PromotionTimeLimit,
  legacyLimitTime?: boolean
): PromotionTimeLimit => {
  if (timeLimit === "midnight" || timeLimit === "60min" || timeLimit === "10min") {
    return timeLimit;
  }
  return legacyLimitTime ? "midnight" : "unlimited";
};

export const buildPromotionLabel = (promotion: {
  type: PromotionDraftType;
  discountPercentage?: number;
  paidUnits?: number;
  receivedUnits?: number;
  timeLimit?: PromotionTimeLimit;
}) => {
  if (promotion.type === "discount") {
    const discountPercentage = Math.min(95, Math.max(1, Number(promotion.discountPercentage || 50)));
    return promotion.timeLimit === "midnight"
      ? `${discountPercentage}% off solo por hoy`
      : `${discountPercentage}% off`;
  }
  if (promotion.type === "buy_x_get_y") {
    const paidUnits = Math.max(1, Number(promotion.paidUnits || 1));
    const receivedUnits = Math.max(paidUnits + 1, Number(promotion.receivedUnits || paidUnits + 1));
    return `Paga ${paidUnits} lleva ${receivedUnits}`;
  }
  return "Promoción especial";
};
